import { useTranslation } from "react-i18next"
import { CheckCircle2, KeyRound, Mail, XCircle } from "lucide-react"

import { Badge, Card, CardContent, Separator } from "@f2e/ui/components/base"
import { useStore } from "@/store"

import {
  UserAvatarUpload,
  UserNameInput,
  UserPasswordChange,
  DataItem,
} from "../components"

interface LoginMethodRowProps {
  icon: React.ReactNode
  label: string
  connected: boolean
  connectedLabel: string
  disconnectedLabel: string
  children?: React.ReactNode
}

const LoginMethodRow = ({
  icon,
  label,
  connected,
  connectedLabel,
  disconnectedLabel,
  children,
}: LoginMethodRowProps) => {
  return (
    <div className="grid gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-800">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-medium">
          {icon}
          <span>{label}</span>
        </div>
        <Badge variant={connected ? "secondary" : "outline"} className="gap-1">
          {connected ? (
            <CheckCircle2 className="h-3.5 w-3.5" />
          ) : (
            <XCircle className="h-3.5 w-3.5" />
          )}
          {connected ? connectedLabel : disconnectedLabel}
        </Badge>
      </div>
      {children}
    </div>
  )
}

const AccountProfileTab = () => {
  const [userMe] = useStore((state) => [state.userMe])
  const { t } = useTranslation()

  return (
    <div className="flex flex-col">
      <h3 className="text-2xl font-semibold leading-none tracking-tight">
        {t("settings.profile.title")}
      </h3>
      <p className="mt-2 mb-4 text-sm text-slate-500 dark:text-slate-400">
        {t("settings.profile.description")}
      </p>
      {userMe && (
        <div className="overflow-hidden overflow-y-auto">
          <Card className="mt-4">
            <CardContent className="flex flex-col gap-2 p-4">
              <DataItem
                title={t("settings.profile.avatar")}
                editable={true}
                valueElement={<UserAvatarUpload user={userMe} />}
              />
              <Separator />
              <DataItem
                title={t("settings.profile.email")}
                valueElement={<div>{userMe.email}</div>}
              />
              <Separator />
              <DataItem
                title={t("settings.profile.name")}
                editable={true}
                valueElement={<UserNameInput name={userMe.name} />}
              />
              <Separator />
              <div className="grid gap-3 py-2">
                <div className="font-medium">
                  {t("settings.profile.loginMethods.title")}
                </div>
                <LoginMethodRow
                  icon={<Mail className="h-4 w-4" />}
                  label={t("settings.profile.loginMethods.google")}
                  connected={Boolean(userMe.hasGoogleLogin)}
                  connectedLabel={t("settings.profile.loginMethods.connected")}
                  disconnectedLabel={t(
                    "settings.profile.loginMethods.notConnected"
                  )}
                />
                <LoginMethodRow
                  icon={<KeyRound className="h-4 w-4" />}
                  label={t("settings.profile.loginMethods.password")}
                  connected={Boolean(userMe.hasUsablePassword)}
                  connectedLabel={t("settings.profile.loginMethods.enabled")}
                  disconnectedLabel={t("settings.profile.loginMethods.notSet")}
                >
                  {userMe.hasUsablePassword ? (
                    <UserPasswordChange />
                  ) : (
                    <UserPasswordChange mode="set" />
                  )}
                </LoginMethodRow>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default AccountProfileTab
